import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";

// DRAFT privacy policy generated from SundayAtlas's actual data flows.
// Placeholders in [brackets] MUST be filled in, and the whole thing
// should be reviewed (ideally by a lawyer) before launch. The draft
// banner below is a reminder; remove it once finalized.

const LAST_UPDATED = "May 2026";

const Privacy = () => {
  return (
    <div className="bg-paper-deep"><div className="mx-auto max-w-[760px] px-5 py-12"><div className="bg-paper border border-rule p-6 sm:p-10">
      <Link
        to="/"
        className="kicker inline-flex items-center gap-2 min-h-[44px] text-ink-muted hover:text-ink transition-colors mb-4"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to SundayAtlas
      </Link>

      <div className="flex items-start gap-2.5 border border-rule bg-paper-warm p-3.5 mb-9">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-accent-text" />
        <span className="font-serif text-sm text-ink-muted leading-relaxed">
          Draft. Replace every [bracketed] placeholder and have this reviewed
          before launch. Remove this banner when finalized.
        </span>
      </div>

      <h1 className="font-serif text-4xl text-ink">Privacy Policy</h1>
      <p className="kicker text-ink-faint mt-3 mb-9">
        Last updated: {LAST_UPDATED}
      </p>

      <div className="space-y-6 font-serif text-[15px] leading-relaxed text-ink [&_h2]:kicker [&_h2]:text-accent-text [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:pt-5 [&_h2]:border-t [&_h2]:border-rule-soft [&_a]:text-accent-text [&_a]:underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_p]:text-ink-muted [&_li]:text-ink-muted [&_strong]:text-ink [&_strong]:font-semibold">
        <p>
          SundayAtlas ("we", "us") is operated by [your legal entity / name],
          [address]. This policy explains what data we process when you use
          the app at [your domain], and your rights over it. Questions:{" "}
          <a href="mailto:[your-email@example.com]">[your-email@example.com]</a>.
        </p>

        <h2>1. What we collect</h2>
        <ul>
          <li>
            <strong>On your device.</strong> When you use SundayAtlas without an
            account, your trips, chat history with the concierge, travel
            preferences, and app settings are stored in your browser's local
            storage and stay on your device, except as described below.
          </li>
          <li>
            <strong>In your account (if you sign in).</strong> If you sign in
            with Google, your trips are also saved to our database (hosted on
            Supabase) so they sync across your devices. We store the trip
            content and your account identifier; access is restricted to your
            account. You can delete trips at any time, which removes them from
            the database.
          </li>
          <li>
            <strong>Sent to our AI provider.</strong> When you chat with the
            concierge, your messages, the current trip state, and your travel
            preferences are sent to Anthropic (Claude) to generate responses.
          </li>
          <li>
            <strong>Place and content lookups.</strong> Destination and place
            names are sent to Google (Places, Maps, Geocoding) to verify
            recommendations, and to the YouTube Data API to surface relevant
            videos.
          </li>
          <li>
            <strong>Shared trips.</strong> If you create a share link, that
            trip (with chat history removed) is stored on our servers (Upstash
            Redis) for up to 180 days so the link works.
          </li>
          <li>
            <strong>Usage analytics and error diagnostics</strong>, only if you
            consent. Anonymous-style product events (e.g., "trip started") and
            error reports that help us fix crashes.
          </li>
          <li>
            <strong>Technical data.</strong> Your IP address is used transiently
            to rate-limit our API endpoints (stored for roughly 60 seconds, then
            discarded). Outbound link clicks (e.g., to a booking site) are
            logged with the target host, a timestamp, and your browser's
            user-agent, to understand usage.
          </li>
        </ul>

        <h2>2. Legal bases (GDPR)</h2>
        <ul>
          <li>
            <strong>Performance of a contract</strong>: processing needed to
            provide the service you requested (building and storing your trip,
            calling the AI).
          </li>
          <li>
            <strong>Consent</strong>: analytics and error diagnostics. You can
            withdraw consent at any time (see "Your rights").
          </li>
          <li>
            <strong>Legitimate interests</strong>: security and abuse
            prevention (rate limiting), and understanding aggregate usage.
          </li>
        </ul>

        <h2>3. Who processes your data (sub-processors)</h2>
        <ul>
          <li>
            <strong>Anthropic</strong> (AI responses):{" "}
            <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>
          </li>
          <li>
            <strong>Google</strong> (Places, Maps, YouTube):{" "}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>
          </li>
          <li>
            <strong>Vercel</strong> (hosting, edge functions):{" "}
            <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>
          </li>
          <li>
            <strong>Upstash</strong> (shared-trip + log storage):{" "}
            <a href="https://upstash.com/trust/privacy.pdf" target="_blank" rel="noopener noreferrer">privacy policy</a>
          </li>
          <li>
            <strong>Sentry</strong> (error diagnostics, with consent):{" "}
            <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer">privacy policy</a>
          </li>
        </ul>

        <h2>4. International transfers</h2>
        <p>
          Some of these providers process data outside the EEA (including the
          United States). Where they do, transfers are covered by Standard
          Contractual Clauses or equivalent safeguards.
        </p>

        <h2>5. How long we keep it</h2>
        <ul>
          <li>On-device data: until you clear it (we never receive it).</li>
          <li>Shared trips: up to 180 days, then auto-deleted.</li>
          <li>Click logs: capped lists, oldest entries dropped over time.</li>
          <li>Rate-limit data: roughly 60 seconds.</li>
        </ul>

        <h2>6. Your rights</h2>
        <p>
          Under the GDPR you have the right to access, rectify, erase, restrict,
          port, and object to processing of your personal data, and to withdraw
          consent. You can also lodge a complaint with your local data
          protection authority. To exercise any of these, contact{" "}
          <a href="mailto:[your-email@example.com]">[your-email@example.com]</a>.
        </p>

        <h2>7. Deleting your data</h2>
        <ul>
          <li>
            On-device data: use "Clear all local data" on your dashboard, or
            clear your browser storage.
          </li>
          <li>
            Shared trips: open the shared link and use "Delete this shared
            trip", or email us the link and we'll remove it.
          </li>
        </ul>

        <h2>8. Cookies and local storage</h2>
        <p>
          We use your browser's local storage to run the app (storing your
          trips and settings). This is essential to the service. We do not use
          advertising cookies. Analytics and error diagnostics run only after
          you accept them in the consent banner.
        </p>

        <h2>9. Children</h2>
        <p>
          SundayAtlas is not directed at children under 16, and we do not
          knowingly collect their data.
        </p>

        <h2>10. Changes</h2>
        <p>
          We may update this policy. Material changes will be reflected by the
          "last updated" date above.
        </p>

        <h2>11. Contact</h2>
        <p>
          [your legal entity / name], [address].{" "}
          <a href="mailto:[your-email@example.com]">[your-email@example.com]</a>.
        </p>
      </div>
    </div></div></div>
  );
};

export default Privacy;
